export interface RenderedPage {
  blob: Blob
  width: number
  height: number
}

/** Pages are drawn about this wide, in pixels: sharp on a large screen and in the full-size viewer, light enough to keep many in memory. */
const TARGET_WIDTH = 1400
/** A document longer than this is cut here: a design is a few pages, and a runaway file must not fill the browser's memory. */
const MAX_PAGES = 60

const canvasToPng = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The page could not be drawn.'))), 'image/png'))

/**
 * Draws every page of a PDF as a picture, with pdf.js. The library and its worker are loaded only when a PDF is opened (they are large, and
 * most designs are pictures), and the worker is bundled by the build so its version always matches the library's.
 */
export async function renderPdfPages(blob: Blob, signal?: AbortSignal): Promise<RenderedPage[]> {
  const pdfjs = await import('pdfjs-dist')
  const { default: workerUrl } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
  const task = pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) })
  const document = await task.promise
  const pages: RenderedPage[] = []
  try {
    for (let number = 1; number <= Math.min(document.numPages, MAX_PAGES); number++) {
      if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError')
      const page = await document.getPage(number)
      const base = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: TARGET_WIDTH / base.width })
      const canvas = window.document.createElement('canvas')
      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      await page.render({ canvas, viewport }).promise
      pages.push({ blob: await canvasToPng(canvas), width: canvas.width, height: canvas.height })
      page.cleanup()
    }
  } finally {
    await document.destroy()
  }
  return pages
}

/**
 * Draws every page of a Word document as a picture. The document is laid out page by page in a hidden box (docx-preview) and each page is then
 * photographed (html-to-image). A browser is not Word, so line and page breaks can differ a little from the original; the result is the
 * same pictures every other screen already knows how to show.
 */
export async function renderDocxPages(blob: Blob, signal?: AbortSignal): Promise<RenderedPage[]> {
  const [{ renderAsync }, { toBlob }] = await Promise.all([import('docx-preview'), import('html-to-image')])
  const host = window.document.createElement('div')
  // Off screen but laid out (a box that is not laid out cannot be photographed), and invisible to people and to the page's own scripts.
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText = 'position:fixed;left:-100000px;top:0;pointer-events:none;'
  window.document.body.append(host)
  try {
    await renderAsync(blob, host, undefined, { breakPages: true, inWrapper: false, ignoreLastRenderedPageBreak: false, renderHeaders: true, renderFooters: true, renderFootnotes: true, useBase64URL: true })
    const sheets = [...host.querySelectorAll<HTMLElement>('section.docx')].slice(0, MAX_PAGES)
    const pages: RenderedPage[] = []
    for (const sheet of sheets) {
      if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError')
      const width = sheet.offsetWidth || 816
      const picture = await toBlob(sheet, { pixelRatio: Math.max(1, TARGET_WIDTH / width), backgroundColor: '#ffffff', cacheBust: false })
      if (picture) pages.push({ blob: picture, width: Math.round(width), height: Math.round(sheet.offsetHeight) })
    }
    return pages
  } finally {
    host.remove()
  }
}

/** Every page of a document, as pictures, whatever it is. A kind that is not a document gives nothing. */
export async function renderDocumentPages(kind: 'pdf' | 'docx', blob: Blob, signal?: AbortSignal): Promise<RenderedPage[]> {
  return kind === 'pdf' ? renderPdfPages(blob, signal) : renderDocxPages(blob, signal)
}
